/**
 * useWalletStream — Real-time wallet balance updates via Server-Sent Events.
 *
 * Opens a single SSE connection per authenticated session. The backend fires
 * two event types:
 *   - `balance.updated`   → directly hydrates the React Query cache for ['wallet', 'balances']
 *   - `transaction.appended` → invalidates ['wallet', 'transactions', *] so the
 *                               transaction list re-fetches automatically
 *
 * Auth: EventSource does not support custom headers. We pass the access token
 * as `?access_token=<jwt>` query param. The backend validates it and never
 * logs the raw value.
 *
 * Reconnect: EventSource has built-in auto-reconnect with exponential back-off.
 * On error we additionally invalidate the wallet cache so the safety-net poll
 * picks up any missed updates.
 *
 * Cleanup: the effect returns a cleanup function that closes the EventSource
 * on unmount (route change or logout). The hook re-fires whenever accessToken
 * or subject.id change so a logout-then-login cycle opens a fresh connection.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.js';
import { WALLET_QUERY_KEYS, type WalletBalances } from './api.js';
import { whenWalletBalanceUnheld } from './balanceHold.js';
import { tenantHeaderValue } from '../../stores/tenant.js';
import { apiBase } from '@/lib/api/host-pool.js';

export function useWalletStream(): void {
  const qc = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const subjectId = useAuthStore((s) => s.subject?.id);

  useEffect(() => {
    if (!accessToken || !subjectId) return;

    // EventSource cannot send custom headers, so in override mode we pass the
    // per-brand tenant id as a query param alongside the access token. In the
    // default Host-resolution path the gateway derives the tenant from the
    // origin, so tenant_id is omitted.
    const tid = tenantHeaderValue();
    const url =
      `${apiBase()}/wallets/me/stream` +
      `?access_token=${encodeURIComponent(accessToken)}` +
      (tid ? `&tenant_id=${encodeURIComponent(tid)}` : '');
    const es = new EventSource(url);

    es.addEventListener('balance.updated', (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data as string) as {
          poolCash: string;
          poolBonus: string;
          poolCommission: string;
          poolCashback: string;
          balance: string;
          withdrawable: string;
          currency: string;
          updatedAt: string;
        };
        // Balances arrive as bigint-string (e.g. "10050"). The existing
        // WalletBalances type uses number (mode:'number' legacy gap — see api.ts
        // comment). Parse to number here to match the existing type contract.
        const balances: WalletBalances = {
          memberId: subjectId,
          creditCash: Number(payload.poolCash),
          creditPromotion: Number(payload.poolBonus),
          commissionWallet: Number(payload.poolCommission),
          cashbackWallet: Number(payload.poolCashback),
          balance: Number(payload.balance),
          withdrawable: Number(payload.withdrawable),
          currency: payload.currency,
          updatedAt: payload.updatedAt,
        };
        whenWalletBalanceUnheld('balance', () => qc.setQueryData<WalletBalances>(WALLET_QUERY_KEYS.balances, balances));
      } catch {
        // Malformed payload — ignore; the safety-net poll will self-correct.
      }
    });

    es.addEventListener('transaction.appended', () => {
      // Append-only: invalidate all transaction query variants so the next
      // render triggers a fresh fetch. Surgical patch would require knowing
      // which filter key is active; invalidation is simpler and correct.
      qc.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
    });

    es.onerror = () => {
      // EventSource reconnects automatically. Invalidate so the fallback poll
      // closes any gap that accumulated while the stream was down.
      whenWalletBalanceUnheld('resync', () => void qc.invalidateQueries({ queryKey: ['wallet'] }));
    };

    return () => {
      es.close();
    };
  }, [accessToken, subjectId, qc]);
}
